/* eslint-disable react/react-in-jsx-scope */
import { CustomDrawer, CustomButton, RestoreIcon } from '@components';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Divider,
  IconButton,
} from '@mui/material';
import { COLORS } from '@styles';
import { appContext } from '@utils';
import { observer } from 'mobx-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import { ClientOrganization, EntitlementType, SaveCustomPriceResult } from '@graphql';
import * as Yup from 'yup';
import { FormikErrors, FormikProps, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { globalSettingsStyles } from '../../../../settings/utils';
import {
  buildInitialPricingValues,
  buildSaveCustomPricesInput,
  SaveCustomPriceInputWithName,
} from './helpers';

const makeStyles = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
    padding: '0 1rem',
    gap: '1.5rem',
  } as React.CSSProperties,
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: COLORS.NEUTRAL_WHITE,
  },
  restoreButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'transparent',
    '&.Mui-disabled': {
      opacity: 0.5,
    },
  },
  restoreButtonText: {
    color: COLORS.ELEKTRAFI_NEUTRAL,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  restoreIcon: {
    color: COLORS.ELEKTRAFI_NEUTRAL,
    width: '1rem',
    height: '0.9375rem',
    flexShrink: 0,
  },
  formItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  label: {
    color: COLORS.NEUTRAL_WHITE,
    fontSize: '0.75rem',
    fontWeight: 500,
    padding: '0 1rem',
  },
  formInputWrapper: {
    borderRadius: '1rem',
  },
  buttonContainer: {
    marginTop: 'auto',
    padding: '1.5rem 0 0 0',
  },
});

const validationSchema = Yup.object({
  prices: Yup.array().of(
    Yup.object({
      id: Yup.string().optional(),
      tierId: Yup.string().optional(),
      featureId: Yup.string().optional(),

      amount: Yup.number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .test('amount-rules', 'Amount must be greater than or equal to 0', function testAmountRules(value) {
          // Retrieve the index of the current item inside the array
          const { path } = this;

          // Rule for the first item: zero is NOT allowed for any tier price
          if (path.toLowerCase().includes('prices[0].amount')) {
            return value !== undefined && value >= 0;
          }

          // Rules for all other items:
          // Zero or undefined are allowed, but positive values also pass
          return value === undefined || value >= 0;
        }),
    }),
  ),
});

interface PlanFormSectionProps {
  /** Formik instance bound to the EditPricingDrawer form */
  formik: FormikProps<{ prices: SaveCustomPriceInputWithName[] }>;
  /** Style objects */
  sx: any;
}

function PlanSeatsSection({ formik, sx }: PlanFormSectionProps) {
  // Tier item is always the first item in the prices array
  // So we can display the price per seat field always even if there are no add-ons
  const tierItem = formik.values.prices[0];

  if (!tierItem) return null;

  return (
    <Box sx={sx.formItems}>
      <Typography variant="body2" sx={sx.label}>
        Price per Seat
      </Typography>

      <TextField
        sx={sx.formInputWrapper}
        fullWidth
        variant="outlined"
        type="number"
        name="prices[0].amount"
        value={tierItem.amount}
        onChange={formik.handleChange}
        InputProps={{
          sx: sx.inputBorder,
          startAdornment: (
            <InputAdornment position="start" sx={sx.inputIcon}>
              <MoneyIcon />
            </InputAdornment>
          ),
        }}
        error={!!(formik.errors.prices?.[0] as FormikErrors<SaveCustomPriceInputWithName>)?.amount}
        helperText={
          (formik.errors.prices?.[0] as FormikErrors<SaveCustomPriceInputWithName>)?.amount
        }
      />
    </Box>
  );
}

// Update PlanAddOnsSection to accept handleRestore and restoreLoading props
function PlanAddOnsSection({
  formik,
  sx,
  handleRestore,
  restoreLoading,
}: PlanFormSectionProps & {
  handleRestore: (id: string | undefined) => () => void;
  restoreLoading: string | null;
}) {
  // Add-ons are all items in the prices array except the first item (tier price)
  const addOns = formik.values.prices.slice(1);

  if (!addOns.length) return null;

  return (
    <>
      {addOns.map((item, index) => {
        const fieldIndex = index + 1; // because index 0 is the tier
        return (
          <Box key={item.featureId || fieldIndex} sx={sx.formItems}>
            <Typography variant="body2" sx={sx.label}>
              {item.name}
            </Typography>
            <TextField
              sx={sx.formInputWrapper}
              fullWidth
              variant="outlined"
              type="number"
              name={`prices[${fieldIndex}].amount`}
              value={formik.values.prices[fieldIndex].amount}
              onChange={formik.handleChange}
              InputProps={{
                sx: sx.inputBorder,
                startAdornment: (
                  <InputAdornment position="start" sx={sx.inputIcon}>
                    <MoneyIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={sx.inputIcon}>
                    <IconButton
                      size="small"
                      sx={{
                        color: COLORS.ELEKTRAFI_LIGHT,
                        padding: 0,
                        '&.Mui-disabled': {
                          opacity: 0.6,
                          cursor: 'not-allowed',
                        },
                      }}
                      onClick={handleRestore(item.id)}
                      disabled={!item.id || restoreLoading === item.id}
                    >
                      <RestoreIcon fontSize="small" sx={sx.restoreIcon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
      })}
    </>
  );
}

interface EditPricingDrawerProps {
  open: boolean;
  onClose: () => void;
  organization: ClientOrganization | null;
}

export const EditPricingDrawer = observer(
  ({ open, onClose, organization }: EditPricingDrawerProps) => {
    const sx = { ...globalSettingsStyles(), ...makeStyles() };
    const {
      tiersStore: { tiers },
      billingStore: {
        getOrgCustomPrices,
        customRatesData,
        saveCustomPrices,
        removeCustomPrice,
        organizationBillingData,
        getOrganizationBilling,
      },
    } = useContext(appContext);
    const { id } = useParams<any>();
    const [restoreLoading, setRestoreLoading] = useState<string | null>(null); // id of price being restored

    const initialPrices = useMemo(
      () =>
        buildInitialPricingValues(
          organization?.currentTier?.id,
          tiers,
          customRatesData?.data ?? [],
          organizationBillingData?.data?.subscriptionDetails?.breakdown ?? [],
        ),
      [
        organization?.currentTier?.id,
        tiers,
        customRatesData?.data,
        organizationBillingData?.data?.subscriptionDetails?.breakdown,
      ],
    );

    const formik = useFormik({
      enableReinitialize: true, // reinitialize when initialPrices changes
      initialValues: { prices: initialPrices },
      validationSchema,
      onSubmit: async (values, { setSubmitting }) => {
        try {
          const toSave = buildSaveCustomPricesInput(values.prices, initialPrices);

          const result = await saveCustomPrices(id, toSave);

          if (result?.every((price: SaveCustomPriceResult) => price.success)) {
            toast('Prices saved successfully', { type: 'success' });
            await getOrganizationBilling(id);
            onClose();
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('error:', error);
        } finally {
          setSubmitting(false);
        }
      },
      validateOnChange: false,
    });
    const { resetForm } = formik;

    useEffect(() => {
      if (id && open) {
        // reset the form to the initial values so we ensure
        // the user can see the initial prices even if they leave the page and come back
        resetForm();
        // get the custom prices from the backend
        getOrgCustomPrices(id);
      }
    }, [id, getOrgCustomPrices, open, resetForm]);

    const currentTier = useMemo(
      () => tiers?.find((t) => t.id === organization?.currentTier?.id),
      [tiers, organization?.currentTier?.id],
    );

    const hasAddOns = useMemo(
      () => currentTier?.entitlements?.some((e) => e.type === EntitlementType.ADD_ON),
      [currentTier],
    );

    const hasChanges = useMemo(
      () => JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues),
      [formik.values, formik.initialValues],
    );

    // Restore a price by id
    const handleRestore = (priceId: string | undefined) => async () => {
      if (!priceId) return;
      setRestoreLoading(priceId);
      try {
        const ok = await removeCustomPrice(priceId, id);
        if (ok) {
          await getOrgCustomPrices(id);
          await getOrganizationBilling(id);
        }
      } catch (e) {
        toast('Failed to restore custom price', { type: 'error' });
      } finally {
        setRestoreLoading(null);
      }
    };

    return (
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Edit Pricing"
        headerIcon={<EditOutlinedIcon />}
      >
        <Box style={sx.wrapper}>
          <Box style={sx.headerRow}>
            <Typography style={sx.sectionTitle}>Plan Seats</Typography>
            <IconButton
              size="small"
              sx={sx.restoreButton}
              onClick={handleRestore(formik.values.prices[0]?.id)}
              disabled={
                !formik.values.prices[0]?.id || restoreLoading === formik.values.prices[0]?.id
              }
            >
              <RestoreIcon fontSize="small" sx={sx.restoreIcon} />
              <Typography variant="body2" style={sx.restoreButtonText}>
                {restoreLoading === formik.values.prices[0]?.id ? 'Restoring...' : 'Restore'}
              </Typography>
            </IconButton>
          </Box>
          {/* tier selection */}
          <Box sx={sx.formItems}>
            <Typography variant="body2" sx={sx.label}>
              Organization Plan
            </Typography>
            <Select
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: COLORS.NEUTRAL_DARK_3,
                  opacity: 0.6,
                  color: COLORS.NEUTRAL_DARK_1,
                },
                ...sx.formInputWrapper,
              }}
              fullWidth
              value={organization?.currentTier?.name}
              disabled
            >
              {tiers?.map((tier) => (
                <MenuItem key={tier.id} value={tier.name}>
                  {tier.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* --- Plan Seats Section --- */}
          <PlanSeatsSection formik={formik} sx={sx} />

          {/* --- Plan Add-ons Section --- */}
          {hasAddOns && (
            <>
              <Box style={sx.headerRow}>
                <Typography style={sx.sectionTitle}>Plan Add-ons</Typography>
              </Box>

              <PlanAddOnsSection
                formik={formik}
                sx={sx}
                handleRestore={handleRestore}
                restoreLoading={restoreLoading}
              />
            </>
          )}

          <Box style={sx.buttonContainer}>
            <Divider style={{ marginTop: '2rem', borderColor: COLORS.NEUTRAL_DARK_1 }} />

            <Box style={sx.buttonContainer}>
              <CustomButton
                variant="default"
                onClick={formik.handleSubmit}
                disabled={!hasChanges || formik.isSubmitting}
              >
                {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </CustomDrawer>
    );
  },
);
